<app-page-header title="Discount Coupon" subtitle="Create and manage promotional discount coupons">
    <div actions class="flex gap-2">
        <p-iconfield>
            <p-inputicon class="pi pi-search" />
            <input type="text" pInputText placeholder="Search" (input)="onGlobalSearch($event)" />
        </p-iconfield>
        <p-button label="Create New" (click)="openCreate()" icon="pi pi-plus"></p-button>
    </div>
</app-page-header>

<div class="table-card">
    <p-table
        #dt
        [value]="coupons"
        [paginator]="true"
        [loading]="loading"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [scrollable]="true"
        scrollHeight="flex"
        dataKey="id"
        [rowHover]="true"
        [globalFilterFields]="['name', 'code', 'amount']"
    >
        <ng-template #header let-columns>
            <tr>
                <th *ngFor="let col of cols" [pSortableColumn]="col.field">
                    <div>
                        {{ col.header }}
                        <p-sortIcon [field]="col.field"></p-sortIcon>
                        <p-columnFilter type="text" [field]="col.field" display="menu"></p-columnFilter>
                    </div>
                </th>
                <th></th>
            </tr>
        </ng-template>
        <ng-template #body let-coupon>
            <tr>
                <td>{{ coupon.name }}</td>
                <td>{{ coupon.code }}</td>
                <td>{{ coupon.amount | number: '1.2-2' }}</td>
                <td>
                    <span [ngClass]="coupon.active ? 'status-badge status-active' : 'status-badge status-inactive'">
                        {{ coupon.active ? 'Active' : 'Inactive' }}
                    </span>
                </td>
                <td>
                    @if (isExpired(coupon.endDate)) {
                        <span class="status-badge status-inactive">Expired</span>
                    } @else {
                        <span class="status-badge status-active">Valid</span>
                    }
                </td>
                <td>{{ coupon.startDate | date: 'dd.MM.yyyy HH:mm' }}</td>
                <td>{{ coupon.endDate | date: 'dd.MM.yyyy HH:mm' }}</td>
                <td>{{ coupon.created | date: 'dd.MM.yyyy HH:mm' }}</td>
                <td>
                    <p-button
                        severity="secondary"
                        icon="pi pi-pencil"
                        (click)="openEdit(coupon)"
                        variant="text"
                        class="p-button-text"
                    ></p-button>
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>

<p-dialog
    [header]="isEditMode ? 'Edit Coupon' : 'New Coupon'"
    [(visible)]="dialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '500px', maxHeight: '100vh' }"
    [appendTo]="'body'"
    [contentStyle]="{ overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }"
>
    <form [formGroup]="couponForm" (ngSubmit)="saveCoupon()">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col">
                <label class="text-sm font-semibold mb-2">Name *</label>
                <input
                    type="text"
                    pInputText
                    placeholder="Write a coupon name"
                    formControlName="name"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('name') }"
                />
                @if (isInvalid('name')) {
                    <small class="text-red-500" *ngIf="couponForm.get('name')?.errors?.['required']">Name is required.</small>
                    <small class="text-red-500" *ngIf="couponForm.get('name')?.errors?.['maxlength']"
                        >Name cannot exceed 255 characters.</small
                    >
                }
            </div>

            <div class="flex flex-col">
                <label class="text-sm font-semibold mb-2">Code *</label>
                <input
                    type="text"
                    pInputText
                    placeholder="Write a coupon code"
                    formControlName="code"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('code') }"
                    maxlength="10"
                    (input)="toUpperCase('code')"
                />
                @if (isInvalid('code')) {
                    <small class="text-red-500" *ngIf="couponForm.get('code')?.errors?.['required']">Code is required.</small>
                    <small class="text-red-500" *ngIf="couponForm.get('code')?.errors?.['maxlength']"
                        >Code cannot exceed 10 characters.</small
                    >
                }
            </div>

            <div class="flex flex-col">
                <label class="text-sm font-semibold mb-2">Amount *</label>
                <p-inputnumber
                    formControlName="amount"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('amount') }"
                ></p-inputnumber>
                @if (isInvalid('amount')) {
                    <small class="text-red-500" *ngIf="couponForm.get('amount')?.errors?.['required']">Amount is required.</small>
                    <small class="text-red-500" *ngIf="couponForm.get('amount')?.errors?.['min']">Amount must be at least 0.</small>
                }
            </div>

            <div class="flex flex-col">
                <label class="text-sm font-semibold mb-2">Start Date (UTC)</label>
                <p-datepicker
                    formControlName="startDate"
                    [showTime]="true"
                    dateFormat="dd.mm.yy"
                    placeholder="Select start date"
                    styleClass="w-full"
                    [appendTo]="'body'"
                ></p-datepicker>
            </div>

            <div class="flex flex-col">
                <label class="text-sm font-semibold mb-2">End Date (UTC)</label>
                <p-datepicker
                    formControlName="endDate"
                    [showTime]="true"
                    dateFormat="dd.mm.yy"
                    placeholder="Select end date"
                    styleClass="w-full"
                    [appendTo]="'body'"
                ></p-datepicker>
            </div>

            <div class="flex items-center gap-2">
                <p-toggleswitch formControlName="active" inputId="activeToggle"></p-toggleswitch>
                <label for="activeToggle">Active</label>
            </div>
        </div>
    </form>
    <ng-template pTemplate="footer">
        <div class="flex justify-between w-full">
            <button type="button" class="text-blue-600 hover:text-blue-800 underline" (click)="clearChanges()">Clear changes</button>
            <div class="flex gap-2">
                <p-button label="Cancel" variant="outlined" (click)="cancelDialog()"></p-button>
                <p-button [label]="isEditMode ? 'Update' : 'Create'" type="submit" (click)="saveCoupon()"></p-button>
            </div>
        </div>
    </ng-template>
</p-dialog>
